import { useEffect, useState } from 'react';
import { Navigate, useNavigate } from 'react-router';
import { api, errorMessage } from '../../lib/api.js';
import { useAuthStore } from '../../lib/auth-store.js';
import { Button, Field, Input } from '../../components/ui/index.js';
import { AuthFrame } from './Login.js';

/**
 * Step 2 of admin sign-in (AC-ADM-01.2). First login enrols: shows the secret
 * as a scannable QR plus the secret as copyable text; subsequent logins
 * verify only.
 */
export function Mfa() {
  const nav = useNavigate();
  const { token, mfaVerified, email, role, signIn, signOut } = useAuthStore();
  const [enrol, setEnrol] = useState<{ secret: string; otpauthUrl: string } | null>(null);
  const [needEnrol, setNeedEnrol] = useState<boolean | null>(null);
  const [qr, setQr] = useState<string | null>(null);
  useEffect(() => {
    if (!enrol) return;
    // Imported here, not at module scope: `qrcode` is 71KB and this is the only
    // screen that renders one, but MFA sits on the sign-in path so the module
    // is in the entry chunk. Every admin paid for it on first load to serve the
    // one-time enrolment step. The link below still works if this never
    // resolves, which is why a failure only clears the image.
    let live = true;
    void import('qrcode')
      .then((m) => m.default.toDataURL(enrol.otpauthUrl, { margin: 1, width: 176 }))
      .then((url) => live && setQr(url))
      .catch(() => live && setQr(null));
    return () => {
      live = false;
    };
  }, [enrol]);
  const [code, setCode] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!token || mfaVerified) return;
    api.admin
      .mfaEnrol({})
      .then((e) => {
        setEnrol(e);
        setNeedEnrol(true);
      })
      .catch(() => setNeedEnrol(false));
  }, [token, mfaVerified]);
  if (!token) return <Navigate to="/login" replace />;
  if (mfaVerified) return <Navigate to="/" replace />;
  return (
    <AuthFrame title="Two-factor authentication" subtitle={email ?? ''}>
      {needEnrol && enrol && (
        <div className="mb-4 rounded-md border bg-grey-100 p-3 text-xs">
          <p className="font-medium text-grey-900">First sign-in: enrol your authenticator app</p>
          <p className="mt-1 text-grey-500">
            Scan with Google Authenticator / 1Password / Authy — or add the secret by hand — then
            enter the 6-digit code.
          </p>
          {qr && (
            <img
              src={qr}
              alt="Authenticator enrolment QR code"
              width={176}
              height={176}
              className="mx-auto mt-3 rounded bg-white p-1"
              data-testid="mfa-qr"
            />
          )}
          <code className="mt-2 block select-all break-all rounded bg-white p-2 font-mono text-[11px] tracking-widest">
            {enrol.secret}
          </code>
          <a className="mt-1 inline-block text-blue-600 underline" href={enrol.otpauthUrl}>
            Open in authenticator
          </a>
        </div>
      )}
      <form
        className="space-y-4"
        onSubmit={async (e) => {
          e.preventDefault();
          setBusy(true);
          setErr(null);
          try {
            const t = await api.admin.mfaVerify({ body: { code } });
            signIn({
              accessToken: t.accessToken,
              role: t.role,
              roleName: t.roleName,
              permissions: t.permissions,
              mfaVerified: t.mfaVerified,
              email: email ?? '',
            });
            nav('/', { replace: true });
          } catch (x) {
            setErr(errorMessage(x));
          } finally {
            setBusy(false);
          }
        }}
      >
        <Field label="6-digit code" required>
          <Input
            inputMode="numeric"
            pattern="\d{6}"
            maxLength={6}
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
            autoFocus
            className="text-center font-mono text-lg tracking-[0.5em]"
          />
        </Field>
        {err && (
          <p className="rounded-md bg-red-600/10 px-3 py-2 text-sm text-red-600" role="alert">
            {err}
          </p>
        )}
        <Button type="submit" className="w-full" loading={busy} disabled={code.length !== 6}>
          Verify
        </Button>
        <button
          type="button"
          onClick={signOut}
          className="w-full text-center text-xs text-grey-500 hover:text-grey-900"
        >
          Use a different account
        </button>
        <p className="text-center text-[11px] text-grey-500">
          Signed in as <span className="capitalize">{role}</span> — no admin action is available
          until verified.
        </p>
      </form>
    </AuthFrame>
  );
}
